/**
 * Reiter „Aus deinen Karten“ der Seite Gedächtnis (Lernen S4, Konzept ~/maestro-uebergabe/konzept-lernen.html,
 * Weg 1): welche Karten und Pakete der Komponist für ein Datum tatsächlich sieht — und welche nicht,
 * mit Grund. Die Regel steht auf dem Server (lib/karten-fuer-datum.ts#kartenGrund, dieselbe wie im
 * Komponisten); hier wird nur angezeigt.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { useApi } from "../hooks/useApi";
import { Card, DateInput, ErrorNote, Icon, LoadingRows, Select } from "../ui/components";

interface KartenZeile {
  id: string;
  name: string;
  kind: string;
  validFrom: string | null;
  validUntil: string | null;
  context: string;
  archiviert: boolean;
  positionen: number;
  ohnePreis: number;
  gilt: boolean;
  grund: string | null;
}
export interface KartenFuerDatum {
  datumIso: string | null;
  ort: "event" | "catering" | null;
  karten: KartenZeile[];
  ohneKarte: number;
  pakete: { id: string; name: string; art: string }[];
}

const tag = (iso: string) => `${iso.slice(8, 10)}.${iso.slice(5, 7)}.${iso.slice(0, 4)}`;
const ZWECK: Record<string, string> = { both: "im Haus und außer Haus", event: "im Haus", catering: "außer Haus" };
const ORT_ALLE = "alle";

function fenster(k: KartenZeile): string {
  if (k.validFrom && k.validUntil) return `gilt ${tag(k.validFrom)} bis ${tag(k.validUntil)}`;
  if (k.validFrom) return `gilt ab ${tag(k.validFrom)}`;
  if (k.validUntil) return `gilt bis ${tag(k.validUntil)}`;
  return "immer gültig";
}

function heuteIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export function KartenTab() {
  const [datum, setDatum] = useState(heuteIso());
  const [ort, setOrt] = useState<string>(ORT_ALLE);
  const qs = new URLSearchParams();
  if (datum) qs.set("datum", datum);
  if (ort !== ORT_ALLE) qs.set("ort", ort);
  const q = useApi<KartenFuerDatum>(`/api/gedaechtnis/karten?${qs.toString()}`);
  const d = q.data;
  const gilt = d?.karten.filter((k) => k.gilt) ?? [];
  const nicht = d?.karten.filter((k) => !k.gilt) ?? [];

  return (
    <div className="flex flex-col gap-6" data-testid="karten-tab">
      <Card className="space-y-4 p-5">
        <div>
          <h2 className="font-body-lg text-body-lg font-semibold text-on-background dark:text-inverse-on-surface">Aus deinen Karten und Paketen</h2>
          <p className="mt-1 font-body-sm text-body-sm text-on-surface-variant">
            Das schlägt der Komponist für eine Veranstaltung an diesem Datum vor. Maßgeblich sind die Einstellungen der Karte: aktiv, „für KI-Vorschläge“, Zeitraum und Zweck.
          </p>
        </div>
        <div className="grid gap-3 sm:grid-cols-[200px_240px]">
          <label className="block">
            <span className="mb-1 block font-body-sm text-body-sm font-semibold text-on-surface dark:text-inverse-on-surface">Veranstaltungsdatum</span>
            <DateInput value={datum} onValueChange={setDatum} aria-label="Veranstaltungsdatum" />
          </label>
          <label className="block">
            <span className="mb-1 block font-body-sm text-body-sm font-semibold text-on-surface dark:text-inverse-on-surface">Wo</span>
            <Select
              ariaLabel="Wo"
              value={ort}
              onValueChange={setOrt}
              options={[
                { value: ORT_ALLE, label: "Egal (Zweck nicht prüfen)" },
                { value: "event", label: "Im Haus" },
                { value: "catering", label: "Außer Haus (Catering)" },
              ]}
            />
          </label>
        </div>
      </Card>

      {q.error ? (
        <Card className="overflow-hidden"><ErrorNote zentriert title="Die Karten konnten nicht geladen werden" message={q.error} onRetry={q.reload} /></Card>
      ) : !d ? (
        <Card className="overflow-hidden"><LoadingRows rows={4} /></Card>
      ) : (
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)] lg:items-start">
          <Card className="space-y-3 p-5">
            <h3 className="font-body-md text-body-md font-semibold text-on-background dark:text-inverse-on-surface">
              Verwendet{d.datumIso ? ` für ${tag(d.datumIso)}` : ""}
            </h3>
            {gilt.length === 0 ? (
              <p className="font-body-sm text-body-sm text-on-surface-variant">Für dieses Datum gilt keine Karte. Der Komponist hätte nur Positionen ohne Karte und Pakete.</p>
            ) : (
              <ul className="space-y-2.5">
                {gilt.map((k) => (
                  <li key={k.id} className="rounded-xl border border-outline-variant px-3.5 py-3 dark:border-white/10" data-testid="karte-gilt">
                    <div className="flex items-start gap-2">
                      <Icon name="menu_book" className="mt-0.5 shrink-0 text-[18px] text-tertiary" />
                      <div className="min-w-0">
                        <p className="break-words font-body-md text-body-md font-semibold text-on-background dark:text-inverse-on-surface">
                          {k.name}
                          {k.archiviert ? <span className="ml-1.5 text-[12px] font-normal text-on-surface-variant">(archiviert)</span> : null}
                        </p>
                        <p className="mt-0.5 text-[12.5px] text-on-surface-variant">
                          {fenster(k)} · {ZWECK[k.context] ?? k.context} · {k.positionen} {k.positionen === 1 ? "Position" : "Positionen"}
                          {k.ohnePreis > 0 ? `, ${k.ohnePreis} ohne Preis (schlägt die KI nicht vor)` : ""}
                        </p>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
            {d.ohneKarte > 0 && (
              <p className="text-[12.5px] text-on-surface-variant">
                Dazu {d.ohneKarte} {d.ohneKarte === 1 ? "Position" : "Positionen"} ohne Karte (Ausstattung, Personal, Sonstiges) — immer verfügbar.
              </p>
            )}
            {nicht.length > 0 && (
              <div className="rounded-xl bg-surface-container-low px-3.5 py-3 dark:bg-white/[0.04]">
                <p className="text-[12.5px] font-semibold text-on-background dark:text-inverse-on-surface">Nicht verwendet für dieses Datum</p>
                <ul className="mt-1 space-y-0.5 text-[12.5px] text-on-surface-variant">
                  {nicht.map((k) => (
                    <li key={k.id} className="break-words" data-testid="karte-nicht">
                      {k.name}: {k.grund}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <Link to="/katalog" className="inline-flex items-center gap-1 text-[12.5px] font-semibold text-primary hover:underline">
              Karten bearbeiten <Icon name="arrow_forward" className="text-[15px]" />
            </Link>
          </Card>
          <Card className="space-y-3 p-5">
            <h3 className="font-body-md text-body-md font-semibold text-on-background dark:text-inverse-on-surface">{d.pakete.length} {d.pakete.length === 1 ? "Paket" : "Pakete"}</h3>
            {d.pakete.length === 0 ? (
              <p className="font-body-sm text-body-sm text-on-surface-variant">Keine Pakete angelegt.</p>
            ) : (
              <ul className="flex flex-wrap gap-1.5">
                {d.pakete.map((p) => (
                  <li key={p.id} className="rounded-full bg-surface-container-high px-2.5 py-1 text-[12.5px] text-on-surface dark:bg-white/10 dark:text-inverse-on-surface">{p.name}</li>
                ))}
              </ul>
            )}
            <p className="text-[12px] text-on-surface-variant">Pakete gelten immer, solange sie nicht archiviert sind. Den Preis setzt das Paket, nie die KI.</p>
          </Card>
        </div>
      )}
    </div>
  );
}
